import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, X, FlipHorizontal, Zap, ZapOff } from "lucide-react";

type Props = {
  onScan: (code: string) => void;
  onClose: () => void;
};

type CamInfo = { id: string; label: string };

export function QrScanner({ onScan, onClose }: Props) {
  const containerId = "qr-scanner-region";
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const activeRef = useRef(false);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(true);
  const [cameras, setCameras] = useState<CamInfo[]>([]);
  const [currentCamId, setCurrentCamId] = useState<string>("");
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);

  // Initialize: enumerate cameras and pick rear by default
  useEffect(() => {
    const instance = new Html5Qrcode(containerId, { verbose: false });
    scannerRef.current = instance;

    (async () => {
      try {
        const devices = await Html5Qrcode.getCameras();
        if (!devices || devices.length === 0) {
          setError("No se detectaron cámaras en este dispositivo.");
          setStarting(false);
          return;
        }
        const list = devices.map((d) => ({ id: d.id, label: d.label || "Cámara" }));
        setCameras(list);
        // prefer rear/back/environment
        const rear = list.find((d) => /back|rear|environment|trasera/i.test(d.label));
        const initial = rear?.id || list[list.length - 1].id;
        setCurrentCamId(initial);
        await startCamera(initial);
      } catch (e: any) {
        setError(
          e?.name === "NotAllowedError" || /permission/i.test(e?.message || "")
            ? "Permiso de cámara denegado. Habilitá el acceso en tu navegador."
            : "No se pudo acceder a la cámara: " + (e?.message || "error desconocido")
        );
        setStarting(false);
      }
    })();

    return () => {
      activeRef.current = false;
      const inst = scannerRef.current;
      if (inst) {
        inst
          .stop()
          .catch(() => {})
          .finally(() => {
            try {
              inst.clear();
            } catch {
              // ignore
            }
          });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function startCamera(camId: string) {
    const inst = scannerRef.current;
    if (!inst) return;
    setError("");
    setStarting(true);
    setTorchOn(false);
    setTorchSupported(false);

    try {
      await inst.start(
        camId,
        {
          fps: 10,
          qrbox: (vw, vh) => {
            const min = Math.min(vw, vh);
            const size = Math.floor(min * 0.7);
            return { width: size, height: size };
          },
          aspectRatio: 1.0,
        },
        (decoded) => {
          if (!activeRef.current) return;
          activeRef.current = false;
          onScan(decoded);
        },
        () => {
          // ignore per-frame failures
        }
      );
      activeRef.current = true;
      setStarting(false);

      // Detect torch capability
      try {
        const caps: any = inst.getRunningTrackCameraCapabilities?.();
        const torchCap = caps?.torchFeature?.();
        if (torchCap?.isSupported?.()) {
          setTorchSupported(true);
        }
      } catch {
        // ignore — capability detection not available
      }
    } catch (e: any) {
      setError(
        e?.name === "NotAllowedError" || /permission/i.test(e?.message || "")
          ? "Permiso de cámara denegado."
          : "No se pudo iniciar la cámara: " + (e?.message || "error desconocido")
      );
      setStarting(false);
    }
  }

  async function switchCamera() {
    if (cameras.length < 2) return;
    const inst = scannerRef.current;
    if (!inst) return;
    const idx = cameras.findIndex((c) => c.id === currentCamId);
    const next = cameras[(idx + 1) % cameras.length];
    activeRef.current = false;
    try {
      await inst.stop();
    } catch {
      // ignore
    }
    setCurrentCamId(next.id);
    await startCamera(next.id);
  }

  async function toggleTorch() {
    const inst = scannerRef.current;
    if (!inst || !torchSupported) return;
    try {
      const caps: any = inst.getRunningTrackCameraCapabilities?.();
      const torchCap = caps?.torchFeature?.();
      if (torchCap?.isSupported?.()) {
        await torchCap.apply(!torchOn);
        setTorchOn(!torchOn);
      }
    } catch (e: any) {
      setError("La linterna no está disponible en esta cámara.");
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background/95 backdrop-blur-sm">
      <div className="flex items-center justify-between border-b border-border px-5 py-4">
        <div className="flex items-center gap-2">
          <Camera className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-bold text-foreground">Escanear QR</h2>
        </div>
        <button
          onClick={onClose}
          className="rounded-lg p-1 text-muted-foreground hover:bg-secondary"
          aria-label="Cerrar"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center px-4 py-6">
        <div
          id={containerId}
          className="w-full max-w-sm overflow-hidden rounded-2xl border border-border bg-black"
        />

        {/* Camera controls */}
        {!error && (
          <div className="mt-4 flex w-full max-w-sm items-center justify-center gap-3">
            <button
              onClick={switchCamera}
              disabled={cameras.length < 2 || starting}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-border bg-secondary px-3 py-2.5 text-xs font-bold text-secondary-foreground transition-all hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Cambiar cámara"
            >
              <FlipHorizontal className="h-4 w-4" />
              Cambiar cámara
            </button>
            <button
              onClick={toggleTorch}
              disabled={!torchSupported || starting}
              className={`flex flex-1 items-center justify-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-bold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                torchOn
                  ? "border-primary/40 bg-primary/20 text-primary"
                  : "border-border bg-secondary text-secondary-foreground hover:bg-accent"
              }`}
              aria-label="Linterna"
            >
              {torchOn ? <Zap className="h-4 w-4" /> : <ZapOff className="h-4 w-4" />}
              {torchOn ? "Linterna ON" : "Linterna"}
            </button>
          </div>
        )}

        {/* Camera selector (when 3+ cameras) */}
        {cameras.length > 2 && !error && (
          <select
            value={currentCamId}
            onChange={async (e) => {
              const id = e.target.value;
              const inst = scannerRef.current;
              if (!inst) return;
              activeRef.current = false;
              try {
                await inst.stop();
              } catch {
                // ignore
              }
              setCurrentCamId(id);
              await startCamera(id);
            }}
            className="mt-3 w-full max-w-sm rounded-lg border border-border bg-input px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {cameras.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        )}

        {starting && !error && (
          <p className="mt-4 text-sm text-muted-foreground">Iniciando cámara…</p>
        )}
        {!starting && !error && (
          <p className="mt-3 text-center text-xs text-muted-foreground">
            Apuntá la cámara al código QR del ticket
            {!torchSupported && " · Linterna no disponible en esta cámara"}
          </p>
        )}
        {error && (
          <p className="mt-4 max-w-sm rounded-lg bg-destructive/10 px-3 py-2 text-center text-xs text-destructive">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
